import React, { FC, useCallback, useMemo } from 'react';
import { Box } from '../../../ui/layout/Box';
import { Flex } from '../../../ui/layout/Flex';
import { ResizableGrid } from '../../../ui/layout/ResizableGrid/ResizableGrid';
import { PostAdCardCompact } from '../../../ui/posts/PostAdCompactCard';
import { PostCardGrid } from '../../../ui/posts/PostCardGrid/PostCardGrid';
import { PostCollectionCardGrid } from '../../../ui/posts/PostCardGrid/PostCollectionCardGrid';
import { Typography } from '../../../ui/typography/Typography';
import { EventSource } from '../../../utils/analyticsService';
import { useIsMobile } from '../../../utils/getIsMobile';
import { useProductsPreviewEnabled } from '../../../utils/useProductsPreviewEnabled';
import { useTranslation } from '../../../utils/useTranslation';
import { useItemsWithAd, useItemsWithoutAd } from '../../utils/useItemsWithAd';
import { withPostCallbacks } from '../withPostCallbacks';
import { AD_ITERVAL, MOBILE_AD_ITERVAL } from './constants';
import { usePostAdCardActions } from './hooks/usePostAdCardActions';
import { PostsGridContentProps } from './types';
import { getItemHeight } from './utils';

// Общий компонент для рендеринга сетки постов
export const PostsGridContent: FC<PostsGridContentProps> = ({
    collection,
    excludePostsIds,
    titleEnabled = false,
    eventSource = EventSource.POST,
    keyPrefix = 'default',
    disableAds = false,
}) => {
    const t = useTranslation();
    const isMobile = useIsMobile();
    const { onPostAdCardCompactAltClick } = usePostAdCardActions();
    const productsPreviewEnabled = useProductsPreviewEnabled();
    const EnchancedPostGridCardCompact = useMemo(
        () => withPostCallbacks(PostCardGrid, eventSource),
        [eventSource],
    );

    const EnhancedCollectionCardGridCompact = useMemo(
        () => withPostCallbacks(PostCollectionCardGrid, eventSource),
        [eventSource],
    );

    const postsWithoutExcludePostsIds = useMemo(
        () => collection.items.filter((post) => !excludePostsIds?.includes(post.id)),
        [excludePostsIds, collection.items],
    );

    const adInterval = isMobile ? MOBILE_AD_ITERVAL : AD_ITERVAL;

    const itemsWithAds = disableAds
        ? useItemsWithoutAd({ items: postsWithoutExcludePostsIds })
        : useItemsWithAd({
              items: postsWithoutExcludePostsIds,
              adTopFeedOffset: adInterval - 1,
              adInterval,
          });

    const onLoadNext = useCallback(() => {
        if (!collection.isEndReached && !collection.isLoading) {
            collection.onLoadNext();
        }
    }, [collection]);

    const getItemHeightCallback = useCallback(
        (index: number, width: number) =>
            getItemHeight(itemsWithAds, index, width, productsPreviewEnabled),
        [itemsWithAds, productsPreviewEnabled],
    );

    const renderItem = useCallback(
        (index: number, columns: number, isVisible?: boolean) => {
            const postModel = itemsWithAds[index];
            if (!postModel) {
                return null;
            }

            if (postModel.isAd) {
                return (
                    <PostAdCardCompact
                        onPostAdCardCompactClick={onPostAdCardCompactAltClick}
                        adInterval={adInterval}
                        postIndex={index}
                    />
                );
            }

            if (postModel.postType === 'collection') {
                return (
                    <EnhancedCollectionCardGridCompact
                        post={postModel}
                        index={index}
                        gridColumns={columns}
                        isVisible={isVisible}
                        hasLogPostViewEvent
                    />
                );
            }

            return (
                <EnchancedPostGridCardCompact
                    post={postModel}
                    index={index}
                    gridColumns={columns}
                    isVisible={isVisible}
                    withProductsPreview={productsPreviewEnabled}
                    hasLogPostViewEvent
                />
            );
        },
        [
            itemsWithAds,
            onPostAdCardCompactAltClick,
            adInterval,
            EnhancedCollectionCardGridCompact,
            EnchancedPostGridCardCompact,
            productsPreviewEnabled,
        ],
    );

    if (isMobile === null) {
        return null;
    }

    return (
        <Flex width="100%" pt={titleEnabled ? 0 : 2}>
            {titleEnabled ? (
                <Typography weight="bold" variant="title2" p={4} pl={isMobile ? 2 : 0}>
                    {itemsWithAds.length > 0 ? t('Вам может понравиться') : ''}
                </Typography>
            ) : null}

            <Box width="100%" px={{ xs: 0.5, lg: 0 }}>
                <ResizableGrid
                    key={keyPrefix}
                    items={itemsWithAds}
                    loadMore={onLoadNext}
                    getItemHeight={getItemHeightCallback}
                    renderItem={renderItem}
                />
            </Box>
        </Flex>
    );
};
